import React, {useContext, useEffect, useState} from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import {GlobalStateContext} from "../state/GlobalStateProvider";
import {
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Grid,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import {withAuthenticationRequired} from "@auth0/auth0-react";
import Loading from "../components/common/Loading";
import {useReservationAPI} from "../service/useReservationAPI";
import {useAnimalAPI} from "../service/useAnimalAPI";
import AppSnackbar from '../common/AppSnackbar';
import {useTranslation} from "react-i18next";
import {useTenantConfig} from "../security/tenant-config-context";


function AnimalNameInput({updateCurrentStep}) {
    const { t } = useTranslation();
    const {globalState, updateGlobalState, setPartOfGlobalState} = useContext(GlobalStateContext);
    const [inputValue, setInputValue] = useState('');
    const [selectedOptions, setSelectedOptions] = useState({});
    const {getAnimalsInReservation} = useReservationAPI();
    const [animals, setAnimals] = useState([])
    const {getAnimals, createOrUpdateAnimal} = useAnimalAPI();
    const [reloadFlag, setReloadFlag] = useState(false);
    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [isLoading, setIsLoading] = useState(true);
    const { cfg } = useTenantConfig();
    const isDogBusiness = cfg?.animalTypes?.includes('DOG');

    const handleInputChange = (event) => {
        setInputValue(event.target.value);
    };

    useEffect(() => {
        setIsLoading(true)
        if (globalState.reservationUuid) {
            // in case existing reservation, possibly not all owned cats are in the reservation
            Promise.all([getAnimalsInReservation(globalState.reservationUuid), getAnimals()]).then(results => {
                const [animalsInReservation, animals] = results;
                const reservedAnimalIds = animalsInReservation.map(animal => animal.id);
                updateGlobalState({reservationMeta: {animalsInReservationIds: reservedAnimalIds}})
                setAnimals(animals)
            }).catch(() => handleFetchError(t('fetchCatsForReservationError')))
                .finally(() => setIsLoading(false));
        } else {
            // case new reservation with existing person with existing cats
            getAllAnimalsAndAddToReservation()
        }
    }, [reloadFlag]);

    function handleFetchError(reason) {
        setSnackbarMessage(reason)
        setOpenSnackbar(true)
    }

    const getAllAnimalsAndAddToReservation = () => {
        getAnimals()
            .then(value => {
                if (value.length > 0) {
                    setAnimals(value);
                }
            })
            .catch(() => handleFetchError(t('fetchMyCatsError')))
            .finally(() => setIsLoading(false));
    }

    const handleAddOption = () => {
        if (inputValue && !animals.map(value => value.name).includes(inputValue)) {
            setSelectedOptions({...selectedOptions, [inputValue]: true});
            setInputValue('');

            setIsLoading(true)
            createOrUpdateAnimal({name: inputValue, type: isDogBusiness ? 'DOG' : 'CAT'})
                .then(() => setReloadFlag(!reloadFlag))
                .catch(() => {
                    handleFetchError(t('addCatError'))
                })
                .finally(() => {
                    setIsLoading(false)
                });
        }
    };

    const handleContinue = () => {
        updateCurrentStep(2)
    };

    const toggleAnimalForReservation = (id) => {
        const {reservationMeta} = globalState;

        const foundReservedAnimal = reservationMeta.animalsInReservationIds.find(value => value === id);

        if (foundReservedAnimal === undefined) {
            const animalToAdd = animals.find(animal => animal.id === id);
            if (animalToAdd) {
                updateGlobalState({
                    reservationMeta: {
                        animalsInReservationIds: [...reservationMeta.animalsInReservationIds, animalToAdd.id],
                        animalsInReservationNames: [...(reservationMeta.animalsInReservationNames || []), animalToAdd.name]
                    }
                });
            }
        } else {
            const idx = reservationMeta.animalsInReservationIds.findIndex(v => v === id);
            const animalsLeft = reservationMeta.animalsInReservationIds.filter(value => value !== id);
            setPartOfGlobalState('reservationMeta.animalsInReservationIds', animalsLeft);
            // Remove the corresponding name at the same index if present
            const names = reservationMeta.animalsInReservationNames || [];
            if (idx >= 0 && idx < names.length) {
                const newNames = names.filter((_, i) => i !== idx);
                setPartOfGlobalState('reservationMeta.animalsInReservationNames', newNames);
            }
        }
    };

    return(
        <>
            {isLoading && (<Loading/>)}
            {!isLoading && (
                <Grid container alignItems="center" justifyContent="center" spacing={1} sx={{textAlign: 'center'}}>
                    <Grid item xs={12} sm={10} md={8} lg={6}>
                        <Card variant="outlined">
                            <CardContent>
                                <>
                                    <Typography variant="h6" color="secondary" gutterBottom sx={{mb: 2}}>
                                        {t('addCatTitle')}
                                    </Typography>
                                    <TextField
                                        required
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        label={t('animalNameLabel')}
                                        variant="outlined"
                                        inputProps={{ "data-testid": "animal-name-input" }}
                                        InputProps={{
                                            endAdornment: (<InputAdornment position="end">
                                                <Button onClick={handleAddOption} variant="contained" disabled={isLoading} data-testid="add-animal-button"><AddIcon/></Button>
                                            </InputAdornment>),
                                        }}
                                    />
                                    <TableContainer component={Paper} sx={{marginTop: 5}}>
                                        <Table aria-label="simple table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><b>{t('selectForStay')}</b></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    animals.length > 0 ? (
                                                        animals.map((animal) => (
                                                            <TableRow key={animal.id} sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                                                <TableCell component="th" scope="row">
                                                                    {/*<PetsIcon color="primary" sx={{marginRight: '10px'}}/> {cat.name}*/}
                                                                    <Checkbox
                                                                        checked={globalState.reservationMeta.animalsInReservationIds.some(value => value === animal.id)}
                                                                        onChange={() => toggleAnimalForReservation(animal.id)}
                                                                        name="spayedNeutered"
                                                                        color="primary"
                                                                        inputProps={{ "data-testid": `select-animal-checkbox-${animal.name}` }}
                                                                    />
                                                                    {animal.name}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell component="th" scope="row" data-testid="no-animals-message">
                                                                {t('addCatPlaceholder')}
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            </CardContent>
                            <CardActions style={{marginBottom: '10px'}}>
                                <Grid item xs={12} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <Button onClick={() => handleContinue()} variant="contained"
                                            disabled={isLoading || globalState.reservationMeta.animalsInReservationIds.length < 1}
                                            data-testid="continue-to-calendar-button">
                                        {t('continueToCalendar')}
                                    </Button>
                                </Grid>
                            </CardActions>
                        </Card>
                        <AppSnackbar openSnackbar={openSnackbar} snackbarMessage={snackbarMessage} setOpenSnackbar={setOpenSnackbar}/>
                    </Grid>
                </Grid>)}
        </>
    )
}


export default withAuthenticationRequired(AnimalNameInput, {
    onRedirecting: () => <Loading/>,
    returnTo: '/nieuwe-reservatie'
});
